import React, { useEffect, useState, useCallback } from 'react';
import { Badge, IconButton } from '@mui/material';
import { keyframes, type SxProps, type Theme } from '@mui/material/styles';
import { Bell } from 'lucide-react';
import { notificationService } from '../service/notificationService';
import { addWsListener } from '../../shared/realtime/ws';

interface Props {
  companyId: string;
  userId?: string;
  onClick?: () => void;
  sx?: SxProps<Theme>;
}

const badgeSoftPulse = keyframes`
  0%, 100% {
    opacity: 1;
    filter: brightness(1);
    box-shadow: 0 0 0 0 rgba(179, 121, 14, 0.55);
  }
  50% {
    opacity: 0.72;
    filter: brightness(1.25);
    box-shadow: 0 0 6px 2px rgba(179, 121, 14, 0.45);
  }
`;

const bellSoftPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
`;

export const NotificationBell: React.FC<Props> = ({ companyId, userId, onClick, sx }) => {
  const [count, setCount] = useState<number>(0);
  const hasNotifications = count > 0;

  const refresh = useCallback(async () => {
    try {
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      if (!companyId) { setCount(0); return; }
      const c = await notificationService.countOpen(companyId, userId);
      setCount(c);
    } catch (e) {
      // ignore
    }
  }, [companyId, userId]);

  // carga inicial (1x)
  useEffect(() => {
    refresh();
  }, [refresh]);

  // sem polling: atualiza só quando chegar WS
  useEffect(() => {
    console.log("🔔 Bell subscribing to WS. companyId=", companyId);

    const unsub = addWsListener((msg: any) => {
      // log
      console.log("🔔 Bell got WS:", msg);
    
      // 🔥 quando chega notificação nova OU quando atualizou (like/dislike)
      if (
        (msg?.type === "NOTIFICATION" || msg?.type === "NOTIFICATION_UPDATED") &&
        msg?.company_id === companyId
      ) {
        refresh(); // chama countOpen e atualiza badge
      }
    });

    return () => {
      console.log("🔕 Bell unsubscribing");
      unsub();
    };
  }, [companyId, refresh]);

  return (
    <IconButton
      color="inherit"
      onClick={onClick}
      size="small"
      aria-label="notificações"
      sx={sx}
    >
      <Badge
        badgeContent={count}
        invisible={!hasNotifications}
        sx={{
          display: 'flex',
          alignItems: 'center',
          '& .MuiBadge-badge': {
            backgroundColor: hasNotifications ? '#b3790e' : '#9e9e9e',
            color: 'white',
            ...(hasNotifications && {
              animation: `${badgeSoftPulse} 1.6s ease-in-out infinite`,
            }),
          },
          ...(hasNotifications && {
            '& svg': {
              animation: `${bellSoftPulse} 1.6s ease-in-out infinite`,
            },
          }),
        }}
      >
        <Bell size={18} />
      </Badge>
    </IconButton>
  );
};




